'use strict';

// external imports
import React, {
    Component,
    MouseEvent
} from 'react';
import {
    bindActionCreators,
    Dispatch
} from 'redux';
import {connect} from 'react-redux';
import parse from 'html-react-parser';

// prime react imports
import {confirmPopup} from 'primereact/confirmpopup';
import {Toolbar as PrimeToolbar} from 'primereact/toolbar';

// shared imports
import {__} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';
import User from '~/shared/src/model/User';
import {RequestConfig} from '~/shared/src/utilities/modules/form';

// frontend imports
import TreeView from '~/frontend/src/modules/panels/TreeView';
import {
    StoreState,
    Thunk
} from '~/frontend/src/store';
// TODO: switch to target component props (TreeView or so)...
import {ComponentProps as DetailProps} from '~/frontend/src/modules/panels/Finder/detail';
import {
    isDataModified,
    isDataModifiedRecursive
} from '~/frontend/src/utilities/dataUtils';
import {
    getToolbarComponents,
    ToolbarComponents,
    ToolbarLayoutComponents
} from '~/frontend/src/utilities/toolbars';

// action imports
import closePanel, {ClosePanel} from '~/frontend/src/actions/panelManager/closePanel';
import dataSaveData, {SaveData as DataSaveData} from '~/frontend/src/actions/data/saveData';
import * as PanelActions from '~/frontend/src/actions/panel/common';
import {SetParamAction} from '~/frontend/src/actions/panel/common/setParam';
import {SaveData} from '~/frontend/src/actions/panel/treeView/saveData';
import {SetAdvancedSearchOptions} from '~/frontend/src/actions/panel/finderDetail/setAdvancedSearchOptions';
import Item from '~/shared/src/ci/Item';

// this quirk is needed due to problems with circular dependencies
let saveData = require( '~/frontend/src/actions/panel/treeView/saveData' ).default;


const Actions = Object.assign( { saveData, dataSaveData, closePanel }, PanelActions ) as any;
type SetParam = <K extends keyof DetailProps>( panelKey : string, paramKey : K, paramValue : DetailProps[K] ) => SetParamAction;


const defaultProps = {
    /**
     * Should the save Button shown in the toolbar <strong>or</strong> not.
     */
    showSaveButton: true as boolean,

    /**
     * The error count to show as badge on save button.
     *
     * Only applicable if <prop>showSaveButton</prop> is set to true.
     */
    saveButtonErrorCount: 0 as number,

    /**
     * The error tooltip to show on save button.
     *
     * Only applicable if <prop>showSaveButton</prop> is set to true AND <prop>saveButtonErrorCount</prop> is set to an positive integer.
     */
    saveButtonErrorTooltip: '' as string | JSX.Element | ( string | JSX.Element )[],

    /**
     * Show copy XML button.
     */
    showCopyXmlButton: true as boolean,

    /**
     * Should the delete Button shown in the toolbar <strong>or</strong> not.
     */
    showDeleteButton: true as boolean,

    /**
     * Show download XML button.
     */
    showDownloadXmlButton: true as boolean,

    /**
     * Show download XML button.
     */
    showDownloadTestTemplateButton: true as boolean,

    showPanelSelectorDropdown: true as boolean,

    /**
     * Show reload button.
     */
    showReloadButton: true as boolean,

    showModifiedHint: false as boolean
};

type DefaultProps = typeof defaultProps;

type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The id of the trees root item
     */
    rootItemId : number;

    /**
     * The database schema to filter data and metadata for.
     */
    schema : Schema;

    dataContext : string;

    /**
     * Event for copy XML button
     */
    onCopyXmlButtonClick? : ( event : MouseEvent<HTMLButtonElement> ) => Promise<void>;

    /**
     * Event for download XML button
     */
    onDownloadXmlButtonClick? : ( event : MouseEvent<HTMLButtonElement> ) => void;

    /**
     * Event for download XML button
     */
    onDownloadTestTemplateButtonClick? : ( event : MouseEvent<HTMLButtonElement> ) => void;

    /**
     * Event for reload button
     */
    onReloadButtonClick? : () => void;

    viewKey? : string;

    saveConfig : RequestConfig;
};

type StateProps = {

    /**
     * The current user.
     *
     * Will be mapped from global state.
     */
    user? : User;

    messageCatalogTimestamp : number;
};

/**
 * Function props mapped by mapDispatchToProps
 */
type ActionPropsRaw = {
    closePanel : ClosePanel;

    dataSaveData : DataSaveData;

    // common panel actions
    setParam : SetParam;

    // treeView actions
    saveData : SaveData;

    setAdvancedSearchOptions : SetAdvancedSearchOptions;
};
type ReplaceReturnType<T extends ( ...a : any ) => any, TNewReturn> = ( ...a : Parameters<T> ) => TNewReturn;
type ActionProps = {
    [K in keyof ActionPropsRaw] : ReplaceReturnType<ActionPropsRaw[K], ReturnType<ActionPropsRaw[K]> extends Thunk<any> ? ReturnType<ReturnType<ActionPropsRaw[K]>> : void>;
};

export type ComponentProps = Partial<DefaultProps> & Props;
type AllProps = DefaultProps & Props & StateProps & ActionProps;


type State = {
    isItemDeletable : boolean;
};


/**
 * Toolbar
 */
class Toolbar extends Component<AllProps, State> {

    static defaultProps = defaultProps;

    /**
     * @param props
     */
    constructor( props : AllProps ) {
        super( props );

        this.state = {
            isItemDeletable: false
        };

        this.onSaveButtonClick = this.onSaveButtonClick.bind( this );
        this.onCopyXmlButtonClick = this.onCopyXmlButtonClick.bind( this );
        this.onDeleteButtonClick = this.onDeleteButtonClick.bind( this );
        this.onDownloadXmlButtonClick = this.onDownloadXmlButtonClick.bind( this );
        this.onDownloadTestTemplateButtonClick = this.onDownloadTestTemplateButtonClick.bind( this );
        this.onReloadButtonClick = this.onReloadButtonClick.bind( this );
    }

    /**
     * REDUX stuff
     */
    static getDerivedStateFromProps( props : AllProps, state : State ) : Partial<State> {
        const retVal : Partial<State> = {
                  isItemDeletable: false
              },
              {
                  dataContext,
                  schema,
                  rootItemId,
                  user
              }                       = props,
              rootItem                = rootItemId ? global.data[ dataContext ]?.items[ rootItemId ] : undefined;

        // editable
        if ( rootItem ) {
            const itemTypeId = rootItem.itemTypeId;

            retVal.isItemDeletable = user?.hasPermissionOnItemType( 'delete', itemTypeId, schema ) ?? false;
        }

        if ( Object.keys( retVal ).length ) {
            return retVal;
        }
        else {
            return {};
        }
    }

    /**
     * Handler for Save button click.
     *
     * @private
     */
    onSaveButtonClick() {
        const saveDataHandler = async ( ...args : Parameters<SaveData> ) => {
            return this.props.saveData( ...args );
        };

        void TreeView.doSaveData( this.props.dataContext, saveDataHandler );
    }

    /**
     * Handler for copy XML button click.
     *
     * @private
     */
    async onCopyXmlButtonClick( event : MouseEvent<HTMLButtonElement> ) {
        if ( this.props.onCopyXmlButtonClick ) {
            await this.props.onCopyXmlButtonClick( event );
        }
    }

    /**
     * Handler for delete Button click.
     *
     * @private
     */
    onDeleteButtonClick() {
        const {
                  uid,
                  dataContext,
                  rootItemId
              }        = this.props,
              rootItem = rootItemId ? global.data[ dataContext ]?.items[ rootItemId ] : undefined;

        if ( rootItem ) {
            const deleteAndSaveFnc = async () => {
                const deleteAndSaveDataContext = `${dataContext}_deleteAndSave`;

                global.data[ deleteAndSaveDataContext ] = {
                    items     : [],
                    references: [],
                    schema    : rootItem.schema
                };

                rootItem.clone( undefined, false, deleteAndSaveDataContext )
                    .reset()
                    .statusId = metaModel[ rootItem.schema ].getStatus( 'deleted' )!.id;

                const result = await this.props.dataSaveData(
                    deleteAndSaveDataContext,
                    uid,
                    'delete',
                    rootItem.schema
                );

                delete global.data[ deleteAndSaveDataContext ];

                if ( result.success ) {
                    void this.props.closePanel( uid );
                }
            }

            void deleteAndSaveFnc();
        }
    }

    /**
     * Handler for download XML button click.
     *
     * @private
     */
    onDownloadXmlButtonClick( event : MouseEvent<HTMLButtonElement> ) {
        if ( this.props.onDownloadXmlButtonClick ) {
            this.props.onDownloadXmlButtonClick( event );
        }
    }

    /**
     * Handler for download test template button
     *
     * @private
     */
    onDownloadTestTemplateButtonClick( event : MouseEvent<HTMLButtonElement> ) {
        if ( this.props.onDownloadTestTemplateButtonClick ) {
            this.props.onDownloadTestTemplateButtonClick( event );
        }
    }

    /**
     * Handler for reload Button click.
     *
     * @private
     */
    onReloadButtonClick( event : MouseEvent<HTMLButtonElement> ) {
        this.props.onReloadButtonClick?.();
    }

    /**
     * REACT lifecycle method
     */
    render() {
        const {
                  uid,
                  dataContext,
                  schema,
                  rootItemId,
                  showSaveButton,
                  saveButtonErrorCount,
                  saveButtonErrorTooltip,
                  showCopyXmlButton,
                  showDeleteButton,
                  showDownloadXmlButton,
                  showDownloadTestTemplateButton,
                  showModifiedHint,
                  showPanelSelectorDropdown,
                  showReloadButton,
                  viewKey
              }                                          = this.props,
              { isItemDeletable }                        = this.state,
              layoutComponents : ToolbarLayoutComponents = {
                  left : [],
                  right: []
              };

        // left
        if ( showSaveButton ) {
            layoutComponents.left.push( {
                type  : 'saveButton',
                saveButtonErrorCount,
                saveButtonErrorTooltip,
                params: {
                    key: `${uid}_saveButton`,

                    onClick: this.onSaveButtonClick
                }
            } );
        }

        if ( showDeleteButton ) {
            layoutComponents.left.push( {
                type  : 'deleteButton',
                params: {
                    key: `${uid}_deleteButton`,

                    disabled: !isItemDeletable,
                    confirm : true,
                    onClick : this.onDeleteButtonClick
                }
            } );
        }

        layoutComponents.left.push( {
            type: 'separator'
        } );

        if ( showCopyXmlButton ) {
            layoutComponents.left.push( {
                type  : 'button',
                params: {
                    key: `${uid}_copyXmlButton`,

                    icon     : 'pi pi-copy',
                    tooltip  : __( 'YN.base.common.copyXml' ),
                    onClick  : ( ...args ) => {
                        void this.onCopyXmlButtonClick( ...args );
                    },
                    className: 'p-button-text yn-copy-xml-button'
                }
            } );
        }

        if ( showDownloadXmlButton ) {
            layoutComponents.left.push( {
                type  : 'button',
                params: {
                    key: `${uid}_downloadXmlButton`,

                    icon     : 'pi pi-download',
                    tooltip  : __( 'YN.base.common.downloadXml' ),
                    onClick  : this.onDownloadXmlButtonClick,
                    className: 'p-button-text yn-download-xml-button'
                }
            } );
        }

        if ( showDownloadTestTemplateButton ) {
            layoutComponents.left.push( {
                type  : 'button',
                params: {
                    key: `${uid}_downloadTestTemplate`,

                    icon     : 'fas fa-flask',
                    tooltip  : __( 'YN.base.downloadTestTemplate.button' ),
                    onClick  : this.onDownloadTestTemplateButtonClick,
                    className: 'p-button-text yn-download-button'
                }
            } );
        }

        layoutComponents.left.push( {
            type: 'separator'
        } );

        if ( showModifiedHint ) {
            layoutComponents.left.push( {
                type  : 'custom',
                params: {
                    key      : `${uid}_modifiedHint`,
                    className: 'flex align-items-center',
                    component: <>
                        <div className="yn-toolbar-modified-hint-icon"/>
                        <span className="ml-2 yn-secondary">{__( 'YN.base.frontend.common.modified.hint' )}</span>
                    </>
                }
            } );
        }


        // right
        if ( showPanelSelectorDropdown ) {
            const item       = rootItemId
                      ? global.data[ dataContext ]?.items[ rootItemId ]
                      : undefined,
                  itemParams = item
                      ? {
                          schema  : item.schema,
                          itemId  : item.id,
                          itemType: item.itemTypeId
                      }
                      : {};

            layoutComponents.right.push( {
                type  : 'panelSelectorDropdown',
                params: {
                    key: `${uid}_panelSelector`,

                    uid        : `${uid}_panelSelector`,
                    labeled    : true,
                    style      : { width: '18rem' },
                    currentView: `TreeView:${viewKey}`,
                    ...itemParams
                }
            } );
        }

        layoutComponents.right.push( {
            type: 'separator'
        } );

        if ( showReloadButton ) {
            layoutComponents.right.push( {
                type  : 'reloadButton',
                params: {
                    key: `${uid}_reloadButton`,

                    confirm: () => rootItemId ? isDataModifiedRecursive( dataContext, schema, rootItemId ) : isDataModified( dataContext, schema! ),
                    onClick: this.onReloadButtonClick
                }
            } );
        }

        const toolbarComponents : ToolbarComponents = getToolbarComponents( layoutComponents );

        return <PrimeToolbar
            left={toolbarComponents.left}
            right={toolbarComponents.right}
            className="yn-panel-toolbar shadow-4"
            style={{ zIndex: 5 }}
        />;
    }
}

/**
 * REDUX stuff
 */
function mapStateToProps( state : StoreState ) : StateProps {
    const retVal : StateProps = {
        user                   : undefined,
        messageCatalogTimestamp: 0
    };

    // get some session user information
    if ( state.session?.user ) {
        retVal.user = state.session.user;
    }

    if ( state.messagecatalog?.timestamp ) {
        retVal.messageCatalogTimestamp = state.messagecatalog.timestamp;
    }

    return retVal;
}

/**
 * REDUX stuff
 */
function mapDispatchToProps( dispatch : Dispatch ) {
    // this quirk is needed due to problems with circular dependencies
    saveData = require( '~/frontend/src/actions/panel/treeView/saveData' ).default;
    return bindActionCreators( Actions, dispatch );
}

export default connect<StateProps, ActionProps, ComponentProps, StoreState>( mapStateToProps, mapDispatchToProps )( Toolbar );
